// Maps the tenant summary (GET /tenants/{tenant}/summary) to the stat tiles. Only this file reads the field
// names of the response, so a registry change is a change here.
import type { TenantLimit, TenantSummary } from "../api/types";
import type { StatTile, StorageUse } from "../components/SummaryTiles";
import { formatBytes } from "./format";

const nf = new Intl.NumberFormat("en-US");
const num = (n: number) => nf.format(n);

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The stat tiles, in a fixed order: setup first (namespaces to members), then content and use. */
export function summaryTiles(s: TenantSummary): StatTile[] {
  const c = s.counts;
  const d = s.downloads;
  return [
    { label: "Namespaces", value: num(c.namespaces) },
    {
      label: "Principals",
      value: num(c.principals),
      // api_keys is a separate count (api_key principals), not a part of principals.
      note: c.api_keys > 0 ? `plus ${plural(c.api_keys, "API key", "API keys")}` : undefined,
    },
    { label: "Grants", value: num(c.grants) },
    { label: "Trust configs", value: num(c.trust_configs) },
    {
      label: "Members",
      value: num(c.members),
      note:
        c.pending_invites > 0 ? plural(c.pending_invites, "pending invite", "pending invites") : undefined,
    },
    { label: "Crates", value: num(c.crates) },
    { label: "Versions", value: num(c.versions) },
    // The registry always tracks download bytes (fair-use soft cap); the count may be missing.
    { label: `Downloads in ${d.month}`, value: d.count === null ? formatBytes(d.bytes) : num(d.count) },
  ];
}

export function summaryStorage(s: TenantSummary): StorageUse {
  return { usedBytes: s.storage.used_bytes, quotaBytes: s.storage.quota_bytes };
}

/** "N of M tenants created", or null when the registry gives no valid limit. */
export function tenantLimitText(limit: TenantLimit | null | undefined): string | null {
  if (!limit || !Number.isInteger(limit.used) || !Number.isInteger(limit.max) || limit.max < 1) return null;
  return `${limit.used} of ${limit.max} tenants created`;
}
